import { createApp, ref, reactive } from 'vue';
import store from './store';
import ArgonDashboard from './plugins/argon-dashboard';
import Locales from '../../../resources/js/app-translations.js';
import { createVuetify } from 'vuetify';
import { ClickOutside } from 'vuetify/directives';
import axios from 'axios';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

// Importar estilos para o Vite
import '../sass/argon.scss';
import '../sass/app.scss';
import '../../../resources/css/tailwind.css';

// Importar o módulo de ambiente Vite
import './vite-env.js';

// Importar a configuração do broadcast para Vite
import './vite-broadcast.js';

import { MaskInput } from 'maska';

// Plugins
import TranslationPlugin from '../../../resources/js/plugins/TranslationPlugin';
import StrPlugin from '../../../resources/js/plugins/StrPlugin';
import RoutePlugin from '../../../resources/js/plugins/RoutePlugin';
import LoadingPlugin from './plugins/loadingModal/loadingPlugin';
import StylePlugin from './plugins/stylePlugin';
import MomentPlugin from '../../../resources/js/plugins/MomentPlugin';
import SettingsPlugin from './plugins/settingsPlugin';

import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';

import VCalendar from 'v-calendar';
import 'v-calendar/style.css';

import Dashboard from './components/Dashboard';
import ResetPassword from './components/Auth/ResetPassword';
import Profile from './components/Profile';
import Course from './components/Course';
import Navbar from './components/Navbar';
import Foot from './components/Foot';
import Plans from './components/Plans';
import Cart from './components/Cart';
import Chat from './components/Chat';
import Recurrence from './components/Recurrence';
import Invoices from './components/InvoiceComponents/Invoices';
import Forum from './components/Forum';
import Lesson from './components/Lesson';
import Class from './components/Class';
import Exercise from './components/Exercise';
import FormBuilder from './components/FormBuilderComponents/FormBuilder';
import Home from './components/Home';
import RegisterFormBuilder from './components/Auth/RegisterFormBuilder';
import Tickets from './components/Tickets';
import Glossary from './components/Glossary';
import WikiList from './components/Wiki/WikiList';
import WikiShow from './components/Wiki/WikiShow';
import TicketReply from './components/TicketReply';
import TicketCreate from './components/TicketCreate';
import PlanDetails from './components/PlanDetails';
import Billing from './components/Billing';
import Invoice from './components/Invoice';
import Order from './components/Order';
import ChangePayment from './components/CheckoutComponents/ChangePayment';
import ChangePaymentPagSeguro from './components/CheckoutComponents/ChangePaymentPagSeguro';
import Breadcrumb from './components/Common/Breadcrumb';
import CertificateTagDraggable from './components/Certificate/CertificateTagDraggable';
import CertificateToolbar from './components/Certificate/CertificateToolbar';
import CertificateValidate from './components/Certificate/CertificateValidate';
import SupportEmailFormBuilder from './components/SupportEmailComponents/SupportEmailFormBuilder';
import DashboardPlans from './components/DashboardComponents/DashboardPlans';
import Topic from './components/Topic';
import NotificationsIndex from './components/NotificationsIndex';
import AppWrapper from './components/AppWrapper';
import Login from './components/Login';
import Terms from './components/Terms';
import Post from '@blog/components/pages/Post';
import Category from '@blog/components/pages/Category';
import Tag from '@blog/components/pages/Tag';
import BlogHome from '@blog/components/pages/Home';
import WhatsAppLink from './components/WhatsAppLink';
import VerifyEmailIndex from './components/VerifyEmail/VerifyEmailIndex';
import VerifyEmailAnotherSending from './components/VerifyEmail/VerifyEmailAnotherSending';
import GamificationScore from './components/Gamification/GamificationScore';
import GamificationShowHoleProgress from './components/Gamification/GamificationShowHoleProgress';
import PlansLeadCapture from './components/PlansComponents/PlansLeadCapture';
import CustomPage from './components/CustomPage';
import SpeakersList from '@/components/SpeakersList';
import SpeakersRegister from '@/components/SpeakersRegister';
import StripeUserCreate from './components/StripeUserCreate.vue';

// dev components to test
import DevBroadcast from './components/Dev/DevBroadcast';

// Instanciar Vuetify 3
const vuetify = createVuetify({
    directives: { ClickOutside },
    theme: {
        themes: {
            light: {
                colors: {
                    primary: '#5e72e4',
                    secondary: '#f7fafc',
                    success: '#2dce89',
                    info: '#11cdef',
                    warning: '#fb6340',
                    error: '#f5365c',
                },
            },
        },
    },
});

// Helper manual para carregar script (substitui vue-plugin-load-script)
function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
    });
}

// Carrega o script js do gerencia net dinamicamente
const tokenPaymentTime = parseInt(Math.random() * 1000000);
loadScript(`/app/gerencia-net-js/${tokenPaymentTime}`)
    .then(() => {
        window.$gn = {
            validForm: true,
            processed: false,
            done: {},
            ready(fn) {
                window.$gn.done = fn;
            },
        };
    })
    .catch((error) => {
        // console.error('FAIL TO LOAD GERENCIA NET JS')
    });

// Mocks e helpers de compatibilidade
const VeeValidateMock = {
    install(app) {
        app.directive('validate', {});
        app.config.globalProperties.$validator = {
            validate: () => Promise.resolve(true),
            validateAll: () => Promise.resolve(true),
            verify: () => Promise.resolve(true),
            reset: () => {},
            errors: {
                items: [],
                has: () => false,
                first: () => '',
                collect: () => [],
                add: () => {},
                clear: () => {},
                all: () => []
            }
        };
        app.mixin({
            data() {
                return {
                    errors: {
                        items: [],
                        has: () => false,
                        first: () => '',
                        collect: () => [],
                        add: () => {},
                        clear: () => {},
                        all: () => []
                    },
                    fields: {}
                }
            }
        });
    }
};

const MQPlugin = {
    install(app) {
        const mq = ref('sm');
        const updateMedia = () => {
            const width = window.innerWidth;
            if (width < 576) {
                mq.value = 'sm';
            } else if (width < 992) {
                mq.value = 'md';
            } else {
                mq.value = 'lg';
            }
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', updateMedia);
            updateMedia();
        }
        Object.defineProperty(app.config.globalProperties, '$mq', {
            get: () => mq.value,
        });
    }
};

// Localization (vue-i18n@9)
import { createI18n } from 'vue-i18n';
if (!localStorage.getItem('lang')) localStorage.setItem('lang', 'pt-BR');
const lang = localStorage.getItem('lang');

export const i18n = createI18n({
    legacy: true,
    locale: lang,
    fallbackLocale: 'pt-BR',
    messages: {
        'pt-BR': Locales['pt-BR'] || {},
        en: Locales.en || {},
        es: Locales.es || {},
        pt: Locales.pt || {},
        jp: Locales.jp || {},
        uk: Locales.uk || {},
    },
});

// Default settings to axios
window.axios = axios;

window.axios.defaults.headers.common = {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
    Authorization: `Bearer ${localStorage.getItem('nochalks_token')}`,
};

const toastOptions = {
    transition: 'Vue-Toastification__bounce',
    maxToasts: 5,
    newestOnTop: true,
};

// Instanciar aplicação Vue 3
const app = createApp({
    components: {
        DevBroadcast,
        ResetPassword,
        Recurrence,
        Dashboard,
        Invoices,
        Profile,
        Course,
        Navbar,
        Plans,
        Forum,
        Foot,
        Cart,
        Chat,
        Lesson,
        Class,
        Exercise,
        FormBuilder,
        ChangePayment,
        ChangePaymentPagSeguro,
        RegisterFormBuilder,
        Home,
        Tickets,
        Glossary,
        WikiList,
        WikiShow,
        TicketReply,
        TicketCreate,
        PlanDetails,
        Breadcrumb,
        Billing,
        Invoice,
        Order,
        CertificateTagDraggable,
        CertificateToolbar,
        CertificateValidate,
        SupportEmailFormBuilder,
        DashboardPlans,
        Topic,
        NotificationsIndex,
        AppWrapper,
        Login,
        Terms,
        Post,
        Category,
        Tag,
        BlogHome,
        WhatsAppLink,
        VerifyEmailIndex,
        VerifyEmailAnotherSending,
        GamificationScore,
        GamificationShowHoleProgress,
        PlansLeadCapture,
        CustomPage,
        StripeUserCreate,
        SpeakersList,
        SpeakersRegister,
    },
    data() {
        return {
            loading: false,
        };
    },
    beforeMount() {
        this.loading = true;
    },
    methods: {
        beforePageDestroyed(e) {
            const confirmationMessage = '';
            (e || window.event).returnValue = confirmationMessage;
            if (!confirmationMessage) {
                console.info('TYPE_RELOAD');
                window.axios.get('https://nochalks3php8.io/api/broadcasting/leave');
            }
            return confirmationMessage;
        },
    },
    mounted() {
        this.loading = false;
    },
});

app.use(store);
app.use(vuetify);
app.use(i18n);
app.use(Toast, toastOptions);
app.use(VCalendar, { componentPrefix: 'vc' });
app.use(ArgonDashboard);
app.use(VeeValidateMock);
app.use(MQPlugin);
app.use(SettingsPlugin);
app.use(TranslationPlugin);
app.use(StrPlugin);
app.use(RoutePlugin);
app.use(LoadingPlugin);
app.use(StylePlugin);
app.use(MomentPlugin);

// Componente flag customizado
app.component('flag', {
    props: {
        iso: {
            type: String,
            required: true,
        },
    },
    template: `
        <img 
            :src="'/images/vendor/flag-icon-css/flags/4x3/' + iso.toLowerCase() + '.svg'" 
            :alt="iso" 
            style="width: 20px; height: 15px; object-fit: cover; border-radius: 2px; vertical-align: middle; box-shadow: 0px 1px 3px rgba(0,0,0,0.15); margin-right: 5px;"
        />
    `,
});

// Diretiva v-mask usando maska
app.directive('mask', {
    mounted(el, binding) {
        const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
        if (!input || !binding.value) return;
        input.dataset.maska = binding.value;
        input.dataset.maskaTokens = 'X:.';
        el.__maska = new MaskInput(input);
    },
    updated(el, binding) {
        const input = el.tagName === 'INPUT' ? el : el.querySelector('input');
        if (!input) return;
        if (binding.value !== binding.oldValue) {
            input.dataset.maska = binding.value || '';
        }
    },
    unmounted(el) {
        if (el.__maska) el.__maska.destroy();
    },
});

app.mount('#app');
